import { io, Socket } from "socket.io-client";
import { API_URL } from "../constants/constants";
import { toast } from "../lib/toast";

class SocketService {
  private static instance: SocketService;
  private socket: Socket | null = null;
  private userId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  private constructor() {
    this.initializeConnection();
    this.setupReconnectOnVisibility();
  }

  private initializeConnection(): void {
    const authData = localStorage.getItem("ACTION-AID-ADMIN-AUTH-LOGIN");
    const parsedData = authData
      ? JSON.parse(authData)
      : { data: { _id: null } };
    const storedUserId = parsedData?.data?._id;
    // console.log("Current user ID:", storedUserId);
    if (storedUserId) {
      this.connect(storedUserId);
    }
  }

  private setupReconnectOnVisibility(): void {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        this.initializeConnection();
      }
    });
  }

  static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  connect(userId: string) {
    if (this.socket?.connected && this.userId === userId) return;

    const apiUrl = API_URL;
    if (!apiUrl) {
      console.error("API_URL is not defined");
      return;
    }
    const wsUrl = apiUrl.startsWith("http://")
      ? `ws://${apiUrl.replace("http://", "")}`
      : `wss://${apiUrl.replace("https://", "")}`;

    this.socket = io(wsUrl, {
      transports: ["websocket"],
    });

    if (this.socket) {
      this.socket.emit("register", { userId });

      this.socket.on("registerSuccess", (response: any) => {
        // console.log("Socket connection established:", response);
      });

      this.socket.on("mentionNotification", (data: any) => {
        // console.log("Mention notification:", data);
        toast("info", JSON.stringify(data.message));
      });

      this.socket.on("connect_error", (error: any) => {
        console.error("Socket connection error:", error);
        this.reconnect();
      });
      this.socket.on("error", (error: any) => {
        console.error("Socket error:", error);
        toast("info", JSON.stringify(error));
      });

      this.socket.on("disconnect", () => {
        // console.log("Socket disconnected, attempting to reconnect...");
        this.reconnect();
      });
    }
  }

  private reconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("Max reconnection attempts reached");
      return;
    }

    this.reconnectAttempts++;
    setTimeout(() => {
      if (this.userId) this.connect(this.userId);
    }, 1000 * this.reconnectAttempts);
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.userId = null;
      localStorage.removeItem("userId");
    }
  }

  getSocket() {
    return this.socket;
  }

  joinChatRoom(userId: string, chatRoomId: string) {
    if (!this.socket) return;

    this.socket.emit("joinChatRoom", { userId, chatRoomId });

    this.socket.on("userJoinedChatRoom", (response: any) => {
      // console.log("Joined chat room:", response);
    });
  }

  sendMessage(userId: string, chatRoomId: string, content: string) {
    if (!this.socket) return;

    const messageData = {
      userId,
      chatRoomId,
      message: {
        attachments: [],
        content,
        senderId: userId,
      },
    };
    // console.log(`messageData : ${userId}`)
    // console.log(`messageData : ${chatRoomId}`)
    // console.log(`messageData : ${content}`)

    this.socket.emit("newMessageChatRoom", messageData);
  }

  joinDiscussionBoard(userId: string, discussionBoardId: string) {
    if (!this.socket) return;

    this.socket.emit("joinDiscussionBoard", { userId, discussionBoardId });

    this.socket.on("userJoinedDiscussionBoard", (response: any) => {
      // console.log("Joined discussion board:", response);
    });
  }

  sendCommentToDiscussionBoard(
    discussionBoardId: string,
    userId: string,
    content: string
  ) {
    if (!this.socket) return;

    const commentData = {
      discussionBoardId,
      comment: {
        userId,
        content,
        mentions: [], // Empty for now
      },
    };

    this.socket.emit("newCommentDiscussionBoard", commentData);
  }

  sendReplyToDiscussionBoard(
    discussionBoardId: string,
    commentId: string,
    userId: string,
    content: string
  ) {
    if (!this.socket) return;

    const replyData = {
      discussionBoardId,
      commentId,
      reply: {
        userId,
        content,
        mentions: [],
      },
    };

    this.socket.emit("newReplyDiscussionBoard", replyData);
  }

  joinCommunityForum(userId: string, communityForumId: string) {
    if (!this.socket) return;

    this.socket.emit("joinCommunityForum", { userId, communityForumId });

    this.socket.on("userJoinedCommunityForum", (response: any) => {
      // console.log("Joined Community Forum:", response);
    });
  }

  sendCommentToCommunityForum(
    communityForumId: string,
    userId: string,
    content: string
  ) {
    if (!this.socket) return;

    const commentData = {
      communityForumId,
      comment: {
        userId,
        content,
        mentions: [], // Empty for now
      },
    };

    this.socket.emit("newCommentCommunityForum", commentData);
  }

  sendReplyToCommunityForum(
    communityForumId: string,
    commentId: string,
    userId: string,
    content: string
  ) {
    if (!this.socket) return;

    const replyData = {
      communityForumId,
      commentId,
      reply: {
        userId,
        content,
        mentions: [],
      },
    };

    this.socket.emit("newReplyCommunityForum", replyData);
  }
}

export const socketService = SocketService.getInstance();
